/**
 * StepSafetyEquipment - Wizard Step 5: Safety equipment configuration
 * WBC3-verified implementation with auto-hide mandatory equipment logic
 */

import React from 'react';
import { Shield } from 'lucide-react';
import { Switch } from '../../../../catalyst/checkbox';
import WizardSection from '../WizardSection';
import { parseCategory } from '../../../../../utils/categoryHelpers';
import { 
  getMandatoryEquipment, 
  getImmersionSuitRequirement, 
  getThermalProtectiveAidsRequirement, 
  getLineThrowingRequirement,
  getOperationsBasedEquipment,
  getMOBRequirements,
  validateMOBCompliance
} from './utils/safetyEquipmentLogic';
import { lsaEquipmentOptions, specialEquipment } from './utils/safetyEquipmentData';
import LSAEquipmentSection from './components/LSAEquipmentSection';
import SpecialEquipmentSection from './components/SpecialEquipmentSection';
import MOBRecoveryHierarchical from './components/MOBRecoveryHierarchical';

// Expose safety equipment logic for testing
if (typeof window !== 'undefined') {
  window.safetyEquipmentLogic = {
    getMandatoryEquipment,
    getImmersionSuitRequirement,
    getThermalProtectiveAidsRequirement,
    getLineThrowingRequirement
  };
}

const StepSafetyEquipment = ({ draftConfig, onUpdate, onValidation }) => {
  // Create updateConfig function compatible with existing logic
  const updateConfig = (updates) => {
    if (typeof updates === 'function') {
      onUpdate(updates(draftConfig));
    } else {
      onUpdate(updates);
    }
  };

  // Sync legacy fields to lsaEquipment array on component mount/config changes
  React.useEffect(() => {
    const currentLsaEquipment = draftConfig.lsaEquipment || [];
    const legacyEquipment = [];
    
    // Check legacy fields and add to array if not already present
    if (draftConfig.hasImmersionSuits && !currentLsaEquipment.includes('immersion_suits')) {
      legacyEquipment.push('immersion_suits');
    }
    if (draftConfig.hasLineThrowing && !currentLsaEquipment.includes('line_throwing_apparatus')) {
      legacyEquipment.push('line_throwing_apparatus');
    }
    if (draftConfig.hasThermalProtectiveAids && !currentLsaEquipment.includes('thermal_protective_aids')) {
      legacyEquipment.push('thermal_protective_aids');
    }
    if (draftConfig.lifeboat_fitted && !currentLsaEquipment.includes('lifeboat')) {
      legacyEquipment.push('lifeboat');
    }
    if (draftConfig.rescue_boat_fitted && !currentLsaEquipment.includes('rescue_boat')) {
      legacyEquipment.push('rescue_boat');
    }
    
    // Only update if there are legacy fields to sync
    if (legacyEquipment.length > 0) {
      const syncedLsaEquipment = Array.from(new Set([...currentLsaEquipment, ...legacyEquipment]));
      updateConfig({ lsaEquipment: syncedLsaEquipment });
    }
  }, [draftConfig.hasImmersionSuits, draftConfig.hasLineThrowing, draftConfig.hasThermalProtectiveAids, draftConfig.lifeboat_fitted, draftConfig.rescue_boat_fitted]);

  // Defensive cleanup of inflatable flags when lifejacketType is foam
  React.useEffect(() => {
    if (draftConfig.lifejacketType === 'foam') {
      const needsCleanup = 
        draftConfig.hasInflatableLifejackets === true ||
        (draftConfig.lsaEquipment && draftConfig.lsaEquipment.includes('inflatable_lifejackets'));
      
      if (needsCleanup) {
        const updates = {};
        
        if (draftConfig.hasInflatableLifejackets === true) {
          updates.hasInflatableLifejackets = false;
        }
        
        if (draftConfig.lsaEquipment && draftConfig.lsaEquipment.includes('inflatable_lifejackets')) {
          const cleanedLsaEquipment = draftConfig.lsaEquipment.filter(item => item !== 'inflatable_lifejackets');
          updates.lsaEquipment = cleanedLsaEquipment;
        }

        updateConfig(updates);
      }
    }
  }, [draftConfig.lifejacketType, draftConfig.hasInflatableLifejackets, draftConfig.lsaEquipment]);

  // Get all equipment requirements first
  const mandatoryEquipment = getMandatoryEquipment(draftConfig);
  const { immersionSuitsMandatory, immersionSuitsReason } = getImmersionSuitRequirement(draftConfig);
  const { tpaMandatory, tpaReason, tpaQuantity } = getThermalProtectiveAidsRequirement(draftConfig, immersionSuitsMandatory);
  const { lineThrowingMandatory, lineThrowingReason } = getLineThrowingRequirement(draftConfig);
  const operationsEquipment = getOperationsBasedEquipment(draftConfig);
  
  const category = parseCategory(draftConfig.category);

  // Validation function for required choices
  const validateRequiredChoices = (config) => {
    const missingChoices = [];
    
    // Check if lifejacket type is selected when lifejackets are required
    const hasLifejackets = mandatoryEquipment.some(eq => eq.name.toLowerCase().includes('lifejacket'));
    if (hasLifejackets && !config.lifejacketType) {
      missingChoices.push({
        field: 'lifejacketType',
        label: 'Lifejacket Type',
        message: 'Please select the type of lifejackets carried on your vessel'
      });
    }
    
    // Check MOB recovery equipment using hierarchical validation
    if (config.operatingStatus === 'seagoing') {
      const mobValidation = validateMOBCompliance(config, config.mobEquipment || []);
      if (!mobValidation.isValid) {
        // Add each MOB validation error as a missing choice
        mobValidation.errors.forEach(error => {
          missingChoices.push({
            field: error.field,
            label: `MOB Equipment (${error.category})`,
            message: error.message,
            description: error.description,
            reference: error.reference
          });
        });
      }
    }
    
    return missingChoices;
  };

  // Check current validation state
  const currentValidation = validateRequiredChoices(draftConfig);
  const hasMOBValidationError = currentValidation.some(choice => choice.field === 'mobRecoveryEquipment');

  // Update validation when config changes
  React.useEffect(() => {
    if (onValidation) {
      const missingChoices = validateRequiredChoices(draftConfig);
      onValidation({
        isValid: missingChoices.length === 0,
        missingChoices,
        stepName: 'Safety Equipment'
      });
    }
  }, [draftConfig]); // Only re-run when draftConfig changes, not when onValidation reference changes

  // Ensure required equipment arrays exist
  if (!draftConfig.lsaEquipment) {
    draftConfig.lsaEquipment = [];
  }
  if (!draftConfig.mobEquipment) {
    draftConfig.mobEquipment = [];
  }

  // Auto-add mandatory LSA equipment
  React.useEffect(() => {
    let needsUpdate = false;
    let newEquipment = [...(draftConfig.lsaEquipment || [])];
    
    // 1. Handle Immersion Suits
    if (immersionSuitsMandatory && !newEquipment.includes('immersion_suits')) {
      newEquipment.push('immersion_suits');
      needsUpdate = true;
    } else if (!immersionSuitsMandatory && newEquipment.includes('immersion_suits')) {
      newEquipment = newEquipment.filter(item => item !== 'immersion_suits');
      needsUpdate = true;
    }
    
    // 2. Handle Thermal Protective Aids (TPAs)
    if (tpaMandatory && !newEquipment.includes('thermal_protective_aids')) {
      newEquipment.push('thermal_protective_aids');
      needsUpdate = true;
    } else if (!tpaMandatory && newEquipment.includes('thermal_protective_aids')) {
      newEquipment = newEquipment.filter(item => item !== 'thermal_protective_aids');
      needsUpdate = true;
    }
    
    // 3. Handle Line Throwing Apparatus (pilot boats only)
    if (lineThrowingMandatory && !newEquipment.includes('line_throwing_apparatus')) {
      newEquipment.push('line_throwing_apparatus');
      needsUpdate = true;
    } else if (!lineThrowingMandatory && newEquipment.includes('line_throwing_apparatus')) {
      newEquipment = newEquipment.filter(item => item !== 'line_throwing_apparatus');
      needsUpdate = true;
    }
    
    // Update config if changes needed
    if (needsUpdate) {
      updateConfig({ lsaEquipment: newEquipment });
    }
  }, [immersionSuitsMandatory, tpaMandatory, lineThrowingMandatory, draftConfig.vesselType, draftConfig.category, draftConfig.operatingHours, draftConfig.operatingArea]);

  return (
    <WizardSection>
      {/* Step Introduction */}
      <div className="text-center mb-6">
        <div className="mx-auto flex items-center justify-center h-14 w-14 md:h-12 md:w-12 rounded-full bg-orange-100 mb-3">
          <Shield className="h-7 w-7 md:h-6 md:w-6 text-orange-600" />
        </div>
        <h2 className="text-xl md:text-xl font-bold text-gray-900 mb-2">
          Safety Equipment
        </h2>
        <WizardSection.Text className="max-w-2xl mx-auto">
          Configure your vessel's safety equipment. Mandatory equipment is automatically included based on 
          your vessel category and type. Additional equipment can be selected based on operational requirements.
        </WizardSection.Text>
      </div>

      {/* Regulatory Context */}
      <WizardSection.Callout variant="regulatory" title="WBC3 Safety Equipment Requirements">
        Life-Saving Appliances (LSA) and Fire-Fighting Equipment (FFE) requirements vary by area category, 
        vessel type, and operations. All equipment must comply with WBC3 Chapter 14 (LSA) and Chapter 16 (FFE).
      </WizardSection.Callout>

      {/* Mandatory Equipment Notice */}
      {mandatoryEquipment.length > 0 && (
        <WizardSection.Callout variant="info" title="Auto-Required Safety Equipment">
          <p className="text-sm mb-3">
            The following equipment is automatically included based on your vessel configuration:
          </p>
          <ul className="space-y-2">
            <li className="text-sm">• Lifejackets for 100% of persons on board (WBC3 14.4.4)</li>
            <li className="text-sm">• Fire extinguishers and detection systems (WBC3 Chapter 16)</li>
            <li className="text-sm">• Navigation lights, shapes, and sound signals (WBC3 Chapter 18)</li>
            <li className="text-sm">• VHF Radio with DSC capability (WBC3 Chapter 17)</li>
            {mandatoryEquipment.map((equipment, index) => (
              <li key={index} className="text-sm">
                • {equipment.name} ({equipment.reference})
              </li>
            ))}
            {immersionSuitsMandatory && (
              <li className="text-sm">• {immersionSuitsReason}</li>
            )}
            {tpaMandatory && (
              <li className="text-sm">• Thermal Protective Aids ({tpaQuantity}) - {tpaReason}</li>
            )}
            {lineThrowingMandatory && (
              <li className="text-sm">• Line Throwing Apparatus (2 units) - {lineThrowingReason}</li>
            )}
          </ul>
        </WizardSection.Callout>
      )}

      {/* Equipment Type Selection */}
      <WizardSection.Card>
        <WizardSection.Header>Equipment Type Selection</WizardSection.Header>
        <p className="text-sm text-gray-600 mb-4">
          Select equipment types and configurations. These choices affect maintenance requirements and inspection frequencies.
        </p>
        
        <div className="space-y-4">
          <WizardSection.Field
            label="Lifejacket Type"
            help="Type affects maintenance requirements and inspection frequency"
          >
            <WizardSection.Select
              value={draftConfig.lifejacketType || 'foam'}
              onChange={(e) => {
                const newType = e.target.value;
                const updates = { lifejacketType: newType };
                
                // Clean up inflatable flags when switching to foam
                if (newType === 'foam') {
                  updates.hasInflatableLifejackets = false;
                  
                  // Remove 'inflatable_lifejackets' from lsaEquipment array if present
                  const currentLsaEquipment = draftConfig.lsaEquipment || [];
                  const cleanedLsaEquipment = currentLsaEquipment.filter(item => item !== 'inflatable_lifejackets');
                  if (cleanedLsaEquipment.length !== currentLsaEquipment.length) {
                    updates.lsaEquipment = cleanedLsaEquipment;
                  }
                }
                
                updateConfig(updates);
              }}
            >
              <option value="foam">Foam (Inherent Buoyancy)</option>
              <option value="inflatable">Inflatable (Higher maintenance)</option>
            </WizardSection.Select>
          </WizardSection.Field>

          {/* Liferaft configuration for all categories */}
          {category >= 0 && category <= 6 && (
            <>
              <WizardSection.Field
                label="Liferaft Capacity (persons)"
                help={`Total capacity of all liferafts fitted (must be ≥${
                  draftConfig.vesselType === 'police' 
                    ? '100%' 
                    : category === 0 || (category === 1 && parseInt(draftConfig.maxPersons || 12) >= 16) 
                      ? '200%' 
                      : '100%'
                } POB)`}
              >
                <WizardSection.Input
                  type="number"
                  value={draftConfig.liferaft_capacity || ''}
                  onChange={(e) => updateConfig({ liferaft_capacity: e.target.value })}
                  placeholder="e.g., 12"
                  min="1"
                />
              </WizardSection.Field>

              <div>
                <label className="flex items-center justify-between">
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900">HRU (Hydrostatic Release Unit) Fitted</span>
                    <p className="text-xs text-gray-500 mt-1">Automatic release mechanism for liferaft deployment</p>
                  </div>
                  <Switch
                    checked={draftConfig.hru_fitted || false}
                    onChange={(checked) => updateConfig({ hru_fitted: checked })}
                  />
                </label>
              </div>

              {draftConfig.hru_fitted && (
                <WizardSection.Field
                  label="HRU Type"
                  help="Type affects maintenance intervals and replacement schedules"
                >
                  <WizardSection.Select
                    value={draftConfig.hruType || 'disposable'}
                    onChange={(e) => updateConfig({ hruType: e.target.value })}
                  >
                    <option value="disposable">Disposable (2-4 year replacement)</option>
                    <option value="serviceable">Serviceable (Annual service)</option>
                  </WizardSection.Select>
                </WizardSection.Field>
              )}
            </>
          )}
        </div>
      </WizardSection.Card>

      {/* Police Boat Cat 5-6 Special Alternative */}
      {draftConfig.vesselType === 'police' && (category === 5 || category === 6) && (
        <WizardSection.Card>
          <WizardSection.Header>
            <div className="flex items-center">
              <Shield className="h-5 w-5 text-blue-500 mr-2" />
              Police Boat Cat {category} Special Option
            </div>
          </WizardSection.Header>
          <WizardSection.Callout variant="info" title="WBC3 Annex 3 Section 11.2.2">
            Police boats operating in area category 5 or 6 have a choice between proper liferafts OR ensuring all persons wear dry suits.
          </WizardSection.Callout>
          
          <div className="mt-4">
            <label className="flex items-center justify-between">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Choose Dry Suit Alternative (instead of liferafts)</span>
                <p className="text-xs text-gray-500 mt-1">
                  If selected, all persons must wear dry suits and proper liferafts are not required
                </p>
              </div>
              <Switch
                checked={draftConfig.policeDrySuitAlternative || false}
                onChange={(checked) => updateConfig({ policeDrySuitAlternative: checked })}
              />
            </label>
          </div>
        </WizardSection.Card>
      )}

      {/* Optional Boats */}
      <WizardSection.Card>
        <WizardSection.Header>Additional Boats (Optional)</WizardSection.Header>
        <p className="text-sm text-gray-600 mb-4">
          Additional boats beyond regulatory requirements. These require weekly engine runs and additional maintenance.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="flex items-center justify-between">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Lifeboat Fitted</span>
                <p className="text-xs text-gray-500 mt-1">Vessel has lifeboats (requires weekly engine runs and davit maintenance)</p>
              </div>
              <Switch
                checked={draftConfig.lifeboat_fitted || false}
                onChange={(checked) => {
                  const currentLsaEquipment = draftConfig.lsaEquipment || [];
                  const updatedLsaEquipment = checked 
                    ? [...currentLsaEquipment, 'lifeboat']
                    : currentLsaEquipment.filter(e => e !== 'lifeboat');
                  updateConfig({ 
                    lifeboat_fitted: checked,
                    lsaEquipment: Array.from(new Set(updatedLsaEquipment))
                  });
                }}
              />
            </label>
          </div>

          <div>
            <label className="flex items-center justify-between">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Rescue Boat Fitted</span>
                <p className="text-xs text-gray-500 mt-1">Fast rescue boats (requires weekly engine runs and specialized training)</p>
              </div>
              <Switch
                checked={draftConfig.rescue_boat_fitted || false}
                onChange={(checked) => {
                  const currentLsaEquipment = draftConfig.lsaEquipment || [];
                  const updatedLsaEquipment = checked 
                    ? [...currentLsaEquipment, 'rescue_boat']
                    : currentLsaEquipment.filter(e => e !== 'rescue_boat');
                  updateConfig({ 
                    rescue_boat_fitted: checked,
                    lsaEquipment: Array.from(new Set(updatedLsaEquipment))
                  });
                }}
              />
            </label>
          </div>
        </div>
      </WizardSection.Card>

      {/* LSA Equipment */}
      <LSAEquipmentSection 
        draftConfig={draftConfig}
        updateConfig={updateConfig}
        lsaEquipmentOptions={lsaEquipmentOptions}
        immersionSuitsReason={immersionSuitsReason}
        tpaReason={tpaReason}
        tpaQuantity={tpaQuantity}
        lineThrowingReason={lineThrowingReason}
        immersionSuitsMandatory={immersionSuitsMandatory}
        tpaMandatory={tpaMandatory}
        lineThrowingMandatory={lineThrowingMandatory}
      />

      {/* MOB Recovery Equipment - Hierarchical WBC3-Verified Requirements */}
      <MOBRecoveryHierarchical 
        draftConfig={draftConfig}
        updateConfig={updateConfig}
        showRequiredWarning={hasMOBValidationError}
      />

      {/* Special Equipment */}
      <SpecialEquipmentSection 
        draftConfig={draftConfig}
        updateConfig={updateConfig}
        specialEquipment={specialEquipment}
      />

      {/* Operations-Based Equipment */}
      {operationsEquipment.length > 0 && (
        <WizardSection.Card>
          <WizardSection.Header>Operations-Based Equipment Requirements</WizardSection.Header>
          <WizardSection.Text>
            Additional equipment required based on your vessel's special operations. 
            These are automatically determined from WBC3 regulatory requirements.
          </WizardSection.Text>

          <div className="space-y-3 mt-4">
            {operationsEquipment.map((equipment, index) => (
              <div 
                key={index}
                className={`p-4 rounded-lg border ${
                  equipment.mandatory 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-yellow-50 border-yellow-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium text-gray-900">
                        {equipment.name}
                      </h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        equipment.mandatory 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {equipment.mandatory ? 'Mandatory' : 'Recommended'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {equipment.reason}
                    </p>
                    <p className="text-xs text-cyan-600 mt-1">
                      {equipment.reference}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <WizardSection.Callout variant="info" title="Operations Equipment">
            These equipment requirements are automatically calculated based on your selected operations. 
            Mandatory items cannot be removed and must be included in your vessel configuration.
          </WizardSection.Callout>
        </WizardSection.Card>
      )}


      {/* Help Information */}
      <WizardSection.Callout variant="info" title="What's Next?">
        After configuring safety equipment, we'll set up navigation and communication equipment 
        based on your vessel's area category and operational requirements.
      </WizardSection.Callout>
    </WizardSection>
  );
};

export default StepSafetyEquipment;